import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, ShieldAlert, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import Avatar from "@/components/Avatar";

export default function Chat() {
  const { id } = useParams();
  const nav = useNavigate();
  const [msgs, setMsgs] = useState([]);
  const [me, setMe] = useState(null);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/social/messages/${id}`);
      setMsgs(data.messages);
      setMe(data.me);
      setOther(data.other);
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]);

  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/social/messages", { to_id: id, text: text.trim() });
      setMsgs((m) => [...m, data.message]);
      setText("");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const report = async () => {
    try { await api.post("/social/report", { target_id: id, reason: "Reported from chat" }); toast.success("Reported. Our mods go check am."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const block = async () => {
    try { await api.post("/social/block", { target_id: id }); toast.success("Blocked. You no go hear from them again."); nav("/social"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="flex flex-col h-[100dvh]" data-testid="chat-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-black/60 backdrop-blur-xl">
        <button data-testid="chat-back" onClick={() => nav("/social")} className="p-1"><ArrowLeft size={22} className="text-slate-500" /></button>
        <Avatar src={other?.avatar} size={36} />
        <div className="flex-1 font-display text-lg">{other?.name}</div>
        <button data-testid="report-btn" onClick={report} className="p-2 text-slate-500 hover:text-[#FFD700]"><ShieldAlert size={18} /></button>
        <button data-testid="block-btn" onClick={block} className="p-2 text-slate-500 hover:text-[#FF0055]"><Ban size={18} /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-4 py-4 space-y-2">
        {msgs.length === 0 && <p className="text-center text-sm text-slate-400 mt-10">Say hi. Keep am respectful — mods dey watch. 👀</p>}
        {msgs.map((m) => {
          const mine = m.from_id === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl text-sm ${
                mine ? "bg-[#16A34A] text-white rounded-br-sm" : "bg-[#1A1A1A] text-slate-900 rounded-bl-sm border border-slate-200"
              } ${m.hidden ? "italic opacity-60" : ""}`}>
                {m.text}
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="flex items-center gap-2 px-4 py-3 border-t border-slate-200 bg-black/60 backdrop-blur-xl mb-[72px]">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type message…"
          className="flex-1 bg-white border border-slate-200 rounded-full px-4 py-2.5 text-slate-900 placeholder-zinc-600 focus:border-[#16A34A] focus:outline-none" />
        <button data-testid="chat-send" disabled={busy} className="p-3 rounded-full bg-[#16A34A] text-white disabled:opacity-50"><Send size={18} /></button>
      </form>
    </div>
  );
}
